import React, { useEffect, useRef } from 'react';
import { Animated, Platform, Pressable, View } from 'react-native';

import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const TRACK_WIDTH = 52;
const TRACK_HEIGHT = 32;
const TRACK_BORDER = 2;
const THUMB_SIZE = 24;
const THUMB_INSET = (TRACK_HEIGHT - 2 * TRACK_BORDER - THUMB_SIZE) / 2;
const THUMB_TRAVEL = TRACK_WIDTH - 2 * TRACK_BORDER - 2 * THUMB_INSET - THUMB_SIZE;

// Drawn with Views instead of React Native's Switch, which cannot be sized or given a
// visible off track on web. Same props as before: value, onValueChange(nextValue),
// disabled, trackColor { false, true }, thumbColor. The pressable area is at least 44 high.
const Switch = function ({
	value,
	onValueChange,
	disabled = false,
	trackColor,
	thumbColor,
	accessibilityLabel,
	accessibilityHint,
	testID,
	style,
}) {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const isOn = Boolean(value);
	const position = useRef(new Animated.Value(isOn ? 1 : 0)).current;

	useEffect(() => {
		Animated.timing(position, {
			toValue: isOn ? 1 : 0,
			duration: 150,
			useNativeDriver: Platform.OS !== 'web',
		}).start();
	}, [isOn, position]);

	const onTrackColor = trackColor?.true || colors.primary;
	const offTrackColor = trackColor?.false;
	const resolvedThumbColor = thumbColor || (isOn ? colors.onPrimary : colors.iconMuted);
	const toggle = disabled ? undefined : () => onValueChange?.(!isOn);
	// react-native-web only activates role="switch" with Enter; a switch must also toggle with Space.
	const webKeyProps = Platform.OS === 'web' && toggle ? {
		onKeyDown: event => {
			if (event.key === ' ' || event.key === 'Spacebar') {
				event.preventDefault();
				toggle();
			}
		},
	} : {};

	return (
		<Pressable
			onPress={toggle}
			{...webKeyProps}
			disabled={disabled}
			accessibilityRole="switch"
			accessibilityLabel={accessibilityLabel}
			accessibilityHint={accessibilityHint}
			accessibilityState={{ checked: isOn, disabled }}
			aria-checked={isOn}
			aria-disabled={disabled}
			testID={testID}
			style={[styles.touchArea, disabled && styles.disabled, style]}
		>
			<View
				style={[
					styles.track,
					isOn
						? { backgroundColor: onTrackColor, borderColor: onTrackColor }
						: offTrackColor && { backgroundColor: offTrackColor },
				]}
			>
				<Animated.View
					style={[
						styles.thumb,
						{ backgroundColor: resolvedThumbColor },
						{ transform: [{ translateX: position.interpolate({ inputRange: [0, 1], outputRange: [0, THUMB_TRAVEL] }) }] },
					]}
				/>
			</View>
		</Pressable>
	);
};

// Off: outlined track with a muted thumb (visible on cards in every theme). On: primary track.
// The negative margin keeps the 44 pt touch area from adding height to the row around it.
const createStyles = ({ ui: { colors, radius, touch } }) => ({
	touchArea: {
		minHeight: touch.min,
		marginVertical: (TRACK_HEIGHT - touch.min) / 2,
		minWidth: TRACK_WIDTH,
		justifyContent: 'center',
		alignItems: 'center',
	},
	track: {
		width: TRACK_WIDTH,
		height: TRACK_HEIGHT,
		borderRadius: radius.pill,
		borderWidth: TRACK_BORDER,
		borderColor: colors.iconMuted,
		backgroundColor: colors.chip,
		justifyContent: 'center',
		paddingHorizontal: THUMB_INSET,
	},
	thumb: {
		width: THUMB_SIZE,
		height: THUMB_SIZE,
		borderRadius: radius.pill,
	},
	disabled: {
		opacity: 0.45,
	},
});

export default Switch;
